import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import SundaySchoolLayout from '../SundaySchoolLayout';
import {
  canEditClass,
  canManageDepartment,
  getSelectedDepartmentId,
  getSundaySchoolSession,
  isReadOnlyStaff,
} from '../sundaySchoolSession';

type Notice = {
  id: number;
  title: string;
  sentAt: string;
  targetType: string;
  totalCount: number;
  confirmedCount: number;
};

type Unconfirmed = {
  recipientId: number;
  noticeId: number;
  studentId: number;
  studentName: string;
  noticeTitle: string;
  daysSince: number;
  resentAt: string | null;
};

type ClassRow = {
  id: number;
  name: string;
  icon: string;
};

type TargetOption = {
  key: string;
  label: string;
  targetType: 'all' | 'class';
  targetClassId?: number;
};

export default function SundaySchoolNotices() {
  const session = getSundaySchoolSession();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [unconfirmed, setUnconfirmed] = useState<Unconfirmed[]>([]);
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [resent, setResent] = useState<Record<number, boolean>>({});

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetKey, setTargetKey] = useState('');
  const [confirmRequired, setConfirmRequired] = useState(true);
  const [sent, setSent] = useState(false);
  const writeRef = useRef<HTMLDivElement>(null);

  // 발송 대상: 부서담당자 이상은 부서 전체와 모든 반, 교사는 맡은 반만, 스텝은 발송 불가.
  const manager = canManageDepartment(session);
  const readOnly = isReadOnlyStaff(session);
  const targets: TargetOption[] = [
    ...(manager ? [{ key: 'all', label: '📚 전체', targetType: 'all' as const }] : []),
    ...classes
      .filter((c) => canEditClass(session, c.id))
      .map((c) => ({
        key: `class-${c.id}`,
        label: `${c.icon} ${c.name}`,
        targetType: 'class' as const,
        targetClassId: c.id,
      })),
  ];

  const loadNotices = () => {
    if (!session) return;
    const params = {
      authChurch: session.user.authChurch,
      departmentId: getSelectedDepartmentId(),
      userId: session.user.id,
    };
    axios
      .get(`${MainURL}/sundayschool/notices`, { params })
      .then(({ data }) => {
        if (data.ok) setNotices(data.data);
      })
      .catch((err) => console.error('GET /sundayschool/notices', err));
    axios
      .get(`${MainURL}/sundayschool/notices/unconfirmed`, { params })
      .then(({ data }) => {
        if (data.ok) setUnconfirmed(data.data);
      })
      .catch((err) => console.error('GET /sundayschool/notices/unconfirmed', err));
  };

  useEffect(() => {
    loadNotices();
    if (!session) return;
    axios
      .get(`${MainURL}/sundayschool/church`, {
        params: {
          authChurch: session.user.authChurch,
          departmentId: getSelectedDepartmentId(),
          userId: session.user.id,
        },
      })
      .then(({ data }) => {
        if (data.ok) setClasses(data.data.classes);
      })
      .catch((err) => console.error('GET /sundayschool/church', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToWrite = () => {
    writeRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const onSend = async () => {
    if (!session || !title || !body) return;
    const selected = targets.find((t) => t.key === targetKey) ?? targets[0];
    if (!selected) return;
    try {
      const { data } = await axios.post(`${MainURL}/sundayschool/notices`, {
        authChurch: session.user.authChurch,
        departmentId: getSelectedDepartmentId(),
        userId: session.user.id,
        title,
        body,
        targetType: selected.targetType,
        targetClassId: selected.targetClassId,
        confirmRequired,
      });
      if (data.ok) {
        setSent(true);
        loadNotices();
      }
    } catch (err) {
      console.error('POST /sundayschool/notices', err);
      window.alert(
        axios.isAxiosError(err) ? (err.response?.data?.message ?? '발송하지 못했습니다.') : '발송하지 못했습니다.',
      );
    }
  };

  const onResend = async (u: Unconfirmed) => {
    if (!session) return;
    try {
      const { data } = await axios.post(`${MainURL}/sundayschool/notices/${u.noticeId}/resend`, {
        authChurch: session.user.authChurch,
        departmentId: getSelectedDepartmentId(),
        userId: session.user.id,
        studentId: u.studentId,
      });
      if (data.ok) setResent((r) => ({ ...r, [u.recipientId]: true }));
    } catch (err) {
      console.error('POST /sundayschool/notices/:id/resend', err);
      window.alert(
        axios.isAxiosError(err) ? (err.response?.data?.message ?? '재발송하지 못했습니다.') : '재발송하지 못했습니다.',
      );
    }
  };

  return (
    <SundaySchoolLayout footNote="알림장 관리 · 확인 버튼 + 미확인 자동 재발송">
      <div className="admin-notices">
        <div className="page-head">
          <div>
            <h1>알림장 📣</h1>
            <div className="sub">주보·공지를 학부모에게 보내고, 확인 여부를 받아보세요</div>
          </div>
          {targets.length > 0 && (
            <a
              className="btn btn-primary"
              href="#write"
              onClick={(e) => {
                e.preventDefault();
                scrollToWrite();
              }}
            >
              ✏️ 새 알림장 쓰기
            </a>
          )}
        </div>

        <div className="grid2 notices-layout">
          <div className="card">
            <div className="card-pad">
              <div className="card-title">
                📬 보낸 알림장 <span className="chip">최근 {notices.length}건</span>
              </div>
            </div>
            <div className="row-list sent-list">
              {notices.length === 0 && <div className="muted card-pad">아직 보낸 알림장이 없어요</div>}
              {notices.map((n) => (
                <div className="row" key={n.id}>
                  <span className="avatar avatar-sm av-2">📣</span>
                  <div className="grow">
                    <div className="t">{n.title}</div>
                    <div className="s">
                      {n.sentAt.slice(0, 10)} 발송 · 대상 {n.totalCount}가족
                    </div>
                    <div className="mt8">
                      <span className="badge badge-ok">확인 {n.confirmedCount}</span>{' '}
                      <span className="badge badge-warn">미확인 {n.totalCount - n.confirmedCount}</span>
                    </div>
                    <div className="prog-wrap">
                      <div className="prog">
                        <i
                          style={{
                            width: `${n.totalCount > 0 ? Math.round((n.confirmedCount / n.totalCount) * 100) : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  {n.confirmedCount === n.totalCount && <span className="badge badge-gray">완료</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="card card-pad">
            <div className="card-title">
              ⚠️ 미확인 가족 <span className="more">{unconfirmed.length}건</span>
            </div>
            <div className="row-list">
              {unconfirmed.length === 0 && <div className="muted">모두 확인했어요 🎉</div>}
              {unconfirmed.map((u) => (
                <div className="row" key={u.recipientId}>
                  <span className="avatar av-2">{u.studentName.slice(0, 1)}</span>
                  <div className="grow">
                    <div className="t">{u.studentName} 가족</div>
                    <div className="s">
                      {u.noticeTitle} · {u.daysSince}일째
                    </div>
                  </div>
                  {!readOnly && (
                    <button className="btn btn-sage btn-sm" onClick={() => onResend(u)}>
                      {resent[u.recipientId] || u.resentAt ? '✅ 재발송' : '재발송'}
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="muted mt16 unread-note">🔔 미확인 2일 경과 시 자동 1회 재발송</div>
          </div>
        </div>

        {targets.length === 0 ? (
          <div className="card card-pad mt24 muted">
            {readOnly
              ? '스텝은 알림장을 볼 수만 있어요.'
              : '맡은 반이 지정되지 않아 알림장을 보낼 수 없어요. 부서 담당자에게 문의해 주세요.'}
          </div>
        ) : (
          <div className="card card-pad mt24" id="write" ref={writeRef}>
            <div className="card-title">✏️ 새 알림장 작성</div>
            <div className="field">
              <label>제목</label>
              <input
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="예) 다음 주 주보와 수련회 안내"
              />
            </div>
            <div className="grid2">
              <div className="field">
                <label>발송 대상</label>
                <div className="pick-wrap">
                  {targets.map((t) => (
                    <button
                      key={t.key}
                      className={`pick${(targetKey || targets[0]?.key) === t.key ? ' on' : ''}`}
                      onClick={() => setTargetKey(t.key)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="field">
                <label>확인 버튼</label>
                <div className="seg">
                  <button className={confirmRequired ? 'on' : ''} onClick={() => setConfirmRequired(true)}>
                    사용 (확인 요청)
                  </button>
                  <button className={!confirmRequired ? 'on' : ''} onClick={() => setConfirmRequired(false)}>
                    미사용
                  </button>
                </div>
              </div>
            </div>
            <div className="field">
              <label>내용</label>
              <textarea
                className="textarea"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="알림장 내용을 입력하세요"
              />
            </div>
            <div className="between">
              <span />
              <button className="btn btn-primary" onClick={onSend}>
                {sent ? '✅ 발송 완료! 학부모에게 알림 전송됨' : '발송하기'}
              </button>
            </div>
          </div>
        )}
      </div>
    </SundaySchoolLayout>
  );
}
